import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../contexts/PlanContext';
import { useTheme } from '../contexts/ThemeContext';
import OnboardingModal from './OnboardingModal';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Building2, 
  LayoutDashboard, 
  FolderKanban,
  Home,
  Share2,
  Users,
  ClipboardList,
  Settings,
  LogOut,
  Moon,
  Sun,
  Menu,
  X,
  ChevronRight,
  Key,
  AlertTriangle,
  Contact,
  Bell
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { cn } from '../lib/utils';

const navigation = [
  { name: 'Overview', href: '/dashboard', icon: LayoutDashboard, roles: ['admin', 'management', 'sales'] },
  { name: 'Projects', href: '/projects', icon: FolderKanban, roles: ['admin', 'management', 'sales'] },
  { name: 'Inventory', href: '/inventory', icon: Home, roles: ['admin', 'management', 'sales'] },
  { name: 'Contacts', href: '/contacts', icon: Contact, roles: ['admin', 'management', 'sales'] },
  { name: 'Channels', href: '/channels', icon: Share2, roles: ['admin', 'management'] },
  { name: 'Users', href: '/users', icon: Users, roles: ['admin', 'management'] },
  { name: 'Audit Log', href: '/audit', icon: ClipboardList, roles: ['admin', 'management'] },
  { name: 'API Keys', href: '/settings', icon: Key, roles: ['admin'] },
];

const Sidebar = ({ isOpen, onClose, notifications, unreadCount, showNotifs, setShowNotifs, markRead, markAllRead, dismissNotif }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const filteredNav = navigation.filter(item => {
    if (!item.roles.includes(user?.role)) return false;
    return true;
  });

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside className={cn(
        "fixed left-0 top-0 h-full w-64 bg-card border-r border-border z-50 flex flex-col transition-transform duration-300 lg:translate-x-0",
        isOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        {/* Logo */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <Building2 className="w-6 h-6 text-primary-foreground" />
              </div>
              <div>
                <h1 className="font-bold text-foreground">Nine Degree</h1>
                <p className="text-xs text-muted-foreground">Inventory System</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden"
              onClick={onClose}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {filteredNav.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={onClose}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                  isActive 
                    ? "bg-primary text-primary-foreground" 
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
                data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-5 h-5" />
                {item.name}
              </Link>
            );
          })}
        </nav>
        
        {/* User section */}
        <div className="p-4 border-t border-border">
          <div className="flex items-center justify-between mb-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </Button>
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowNotifs(!showNotifs)}
                data-testid="notification-bell"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-destructive text-destructive-foreground text-[9px] font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
                    {unreadCount}
                  </span>
                )}
              </Button>

              {showNotifs && (
                <div className="absolute bottom-full left-0 mb-2 w-80 bg-popover border border-border rounded-lg shadow-xl z-50 max-h-80 flex flex-col" data-testid="notification-panel">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                    <p className="text-xs font-semibold">Notifications</p>
                    {unreadCount > 0 && (
                      <button className="text-[10px] text-primary hover:underline" onClick={markAllRead} data-testid="mark-all-read">Mark all read</button>
                    )}
                  </div>
                  <div className="overflow-y-auto flex-1">
                    {notifications.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-6">No notifications</p>
                    ) : (
                      notifications.map(n => (
                        <div
                          key={n.notification_id}
                          className={cn(
                            "px-3 py-2.5 border-b border-border/50 last:border-0 transition-colors",
                            !n.read && "bg-primary/5"
                          )}
                          onClick={() => !n.read && markRead(n.notification_id)}
                          data-testid={`notif-${n.notification_id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium">{n.title}</p>
                              <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                              {n.cta_text && (
                                <a href={n.cta_url || '/request-pricing'} className="text-[10px] text-primary font-medium hover:underline mt-1 inline-block" data-testid={`notif-cta-${n.notification_id}`}>
                                  {n.cta_text}
                                </a>
                              )}
                            </div>
                            <button className="text-muted-foreground hover:text-foreground p-0.5 flex-shrink-0" onClick={(e) => { e.stopPropagation(); dismissNotif(n.notification_id); }} data-testid={`notif-dismiss-${n.notification_id}`}>
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                          <p className="text-[9px] text-muted-foreground mt-1">
                            {n.created_at ? new Date(n.created_at).toLocaleDateString() : ''}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors">
                <Avatar className="h-9 w-9">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-primary/10 text-primary">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <Badge variant="secondary" className="text-xs capitalize">{user?.role?.replace('_', ' ')}</Badge>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem className="flex-col items-start">
                <p className="font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>
    </>
  );
};

const Header = ({ onMenuClick }) => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  
  const currentPage = navigation.find(item => item.href === location.pathname);

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-background/80 border-b border-border/40 lg:hidden">
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onMenuClick} data-testid="mobile-menu">
            <Menu className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Building2 className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-semibold">{currentPage?.name || 'Dashboard'}</span>
          </div>
        </div>
        
        <Button variant="ghost" size="icon" onClick={toggleTheme}>
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
      </div>
    </header>
  );
};

const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const { user, refreshUser } = useAuth();
  const { isReadOnly, accessStatus } = usePlan();

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Show onboarding modal if not completed
  useEffect(() => {
    if (user && user.onboarding_completed === false) {
      setShowOnboarding(true);
    }
  }, [user]);

  // Fetch notifications
  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/v1/notifications`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setNotifications(data.notifications || []);
          setUnreadCount(data.unread_count || 0);
        }
      } catch { /* ignore */ }
    };
    load();
    const interval = setInterval(load, 60000); // refresh every minute
    return () => clearInterval(interval);
  }, [user, API_URL]);

  const markRead = async (notifId) => {
    await fetch(`${API_URL}/api/v1/notifications/${notifId}/read`, { method: 'POST', credentials: 'include' });
    setNotifications(prev => prev.map(n => n.notification_id === notifId ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const dismissNotif = async (notifId) => {
    await fetch(`${API_URL}/api/v1/notifications/${notifId}/dismiss`, { method: 'POST', credentials: 'include' });
    setNotifications(prev => prev.filter(n => n.notification_id !== notifId));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await fetch(`${API_URL}/api/v1/notifications/read-all`, { method: 'POST', credentials: 'include' });
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    if (refreshUser) refreshUser();
  };

  const trialEndsAt = user?.trial_ends_at;
  let daysLeft = null;
  let trialExpired = false;
  if (trialEndsAt) {
    const end = new Date(trialEndsAt);
    const now = new Date();
    daysLeft = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    trialExpired = daysLeft <= 0;
  }

  const showTrialBanner = accessStatus === 'trial' && daysLeft !== null && !trialExpired;
  const showExpiredBanner = isReadOnly || trialExpired;

  return (
    <div className="min-h-screen bg-background">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        notifications={notifications}
        unreadCount={unreadCount}
        showNotifs={showNotifs}
        setShowNotifs={setShowNotifs}
        markRead={markRead}
        markAllRead={markAllRead}
        dismissNotif={dismissNotif}
      />
      <Header onMenuClick={() => setSidebarOpen(true)} />
      <main className="lg:ml-64 min-h-screen">
        {showExpiredBanner && (
          <div className="bg-destructive/10 border-b border-destructive/20 px-6 py-3 flex items-center justify-between" data-testid="expired-banner">
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>Your trial has ended. Pricing starts at &#8377;5,000 per month. Contact Nine Degree for a customised proposal and to restore full access.</span>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <a href="/request-pricing" className="text-xs font-medium bg-destructive text-destructive-foreground px-3 py-1.5 rounded hover:bg-destructive/90" data-testid="expired-request-pricing">Request Pricing</a>
              <a href="mailto:support@ninedegree.in" className="text-xs font-medium border border-destructive/30 text-destructive px-3 py-1.5 rounded hover:bg-destructive/10">Contact Support</a>
            </div>
          </div>
        )}
        {showTrialBanner && (
          <div className="bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800/30 px-6 py-2.5 flex items-center justify-between" data-testid="trial-banner">
            <p className="text-xs text-amber-800 dark:text-amber-300">
              <strong>Trial:</strong> {daysLeft} day{daysLeft !== 1 ? 's' : ''} remaining
            </p>
            <a href="/request-pricing" className="text-xs font-medium text-amber-700 dark:text-amber-400 hover:underline" data-testid="trial-request-pricing">
              Request Pricing
            </a>
          </div>
        )}
        <div className="p-6 lg:p-8 max-w-7xl mx-auto">
          {children}
        </div>
      </main>

      <OnboardingModal open={showOnboarding} onComplete={handleOnboardingComplete} />
    </div>
  );
};

export default DashboardLayout;
